import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import {
  Crown,
  Flame,
  Zap,
  Star,
  Heart,
  Sparkles,
  ArrowRight,
  CheckCircle,
  Clock,
  Shield
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import RankPurchaseModal from '../components/RankPurchaseModal';

const RankShop = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [ranks, setRanks] = useState([]);
  const [selectedRank, setSelectedRank] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchRanks();
  }, []);

  const fetchRanks = async () => {
    try {
      const response = await axios.get('/api/rank/available');
      const apiRanks = response.data;

      const mappedRanks = apiRanks.map(rank => {
        const rankConfig = {
          'Vip': {
            fullName: 'وی‌آی‌پی',
            features: ['دسترسی به امکانات پایه VIP', 'امکانات ویژه محدود', 'پشتیبانی پایه'],
            color: 'from-yellow-600 via-orange-600 to-yellow-700',
            icon: Heart,
            image: '/vip.jpg',
            popular: false,
            category: 'roleplay'
          },
          'Mvp': {
            fullName: 'ام‌وی‌پی',
            features: ['دسترسی به امکانات متوسط MVP', 'امکانات ویژه', 'پشتیبانی خوب'],
            color: 'from-purple-600 via-pink-600 to-purple-700',
            icon: Star,
            image: '/mvp.jpg',
            popular: false,
            category: 'roleplay'
          },
          'Dragon': {
            fullName: 'دراگون',
            features: ['دسترسی به امکانات عالی Dragon', 'امکانات ویژه', 'پشتیبانی بالا'],
            color: 'from-red-600 via-orange-600 to-red-700',
            icon: Flame,
            image: '/dragon.jpg',
            popular: true,
            badge: 'محبوب',
            category: 'roleplay'
          },
          'Eternity': {
            fullName: 'اترنیتی',
            features: ['دسترسی به امکانات پیشرفته Eternity', 'امکانات پیشرفته', 'پشتیبانی بالا'],
            color: 'from-green-600 via-teal-600 to-emerald-700',
            icon: Clock,
            image: '/eternity.png',
            popular: false,
            category: 'roleplay'
          },
          'Sponsor': {
            fullName: 'اسپانسر',
            features: ['دسترسی به امکانات اختصاصی Sponsor', 'حامی مالی سرور', 'امکانات اختصاصی', 'پشتیبانی ویژه'],
            color: 'from-green-600 via-teal-600 to-emerald-700',
            icon: Crown,
            image: '/sponsor.jpg',
            popular: false,
            category: 'roleplay'
          },
          'Lite': {
            fullName: 'لایت',
            features: ['دسترسی به امکانات پایه', 'امکانات ویژه محدود', 'پشتیبانی پایه'],
            color: 'from-gray-600 via-blue-600 to-slate-700',
            icon: Sparkles,
            image: '/lite.jpg',
            popular: false,
            category: 'bedwars'
          },
          'Blitz': {
            fullName: 'بلیتز',
            features: ['اقتصادی' ,'دسترسی به امکانات متوسط', 'سرعت بیشتر', 'امکانات ویژه', 'پشتیبانی خوب'],
            color: 'from-blue-600 via-purple-600 to-indigo-700',
            icon: Zap,
            image: '/blitz.jpg',
            popular: true,
            category: 'bedwars'
          },
          'Nebula': {
            fullName: 'نبیولا',
            features: ['سرعت بیشتر',  'اقتصادی' ,'دسترسی به امکانات متوسط', 'امکانات ویژه', 'پشتیبانی بالا'],
            color: 'from-purple-600 via-pink-600 to-purple-700',
            icon: Star,
            image: '/nebula.jpg',
            popular: false,
            category: 'bedwars'
          },
          'Plutonium': {
            fullName: 'پلوتونیوم',
            features: ['سرعت بیشتر' ,'دسترسی به امکانات پیشرفته', 'امکانات پیشرفته', 'پشتیبانی ویژه'],
            color: 'from-green-600 via-teal-600 to-emerald-700',
            icon: Heart,
            image: '/plutonium.jpg',
            popular: false,
            category: 'bedwars'
          },
          'Heaven': {
            fullName: 'هیون (Heaven)',
            features: [
              'امکانات پایه پیلارز',
              '۲ کیت آغازین شوالیه و معدنچی',
              '۳ کیل‌افکت و رقص پیروزی',
              '۳ مدل بال و ۱۵ رنگ زره'
            ],
            color: 'from-slate-300 via-gray-400 to-slate-600',
            icon: Sparkles,
            image: '/heaven.png',
            popular: false,
            category: 'pillars'
          },
          'Crystal': {
            fullName: 'کریستال (Crystal)',
            features: [
              'بوستر ۲ برابری XP و سکه سرور',
              '۳ کیت ویژه چوب‌بر، جادوگر و پزشک',
              '۸ کیج ۳بعدی و ۵ کیل‌افکت حماسی',
              '۵ مدل بال فانتزی و تریل نوری'
            ],
            color: 'from-purple-600 via-fuchsia-600 to-pink-600',
            icon: Zap,
            image: '/crystal.png',
            popular: true,
            badge: 'پیشنهادی',
            category: 'pillars'
          },
          'Pillar': {
            fullName: 'پیلار (Pillar)',
            features: [
              'VIP برتر با دسترسی ۱۰۰٪ نامحدود',
              'بوستر ۳ برابری فوق‌العاده XP',
              'کیت ویژه و قدرتمند تروریست',
              'تمامی کیج‌ها، بال‌ها و کیپ‌های سرور'
            ],
            color: 'from-emerald-600 via-teal-600 to-emerald-800',
            icon: Crown,
            image: '/pillar.png',
            popular: false,
            badge: 'برتر VIP',
            category: 'pillars'
          },
          'L-GroveStreet': {
            fullName: 'گنگستر GroveStreet',
            features: [
              'دوره ۳۰ روزه اختصاصی (تک لیدر)',
              'ظرفیت: ۱ لیدر + ۲ ممبر (مجموع ۳ نفر)',
              'بالاترین رده خلافکاری سرور',
              'دسترسی به تمام بذرهای پیشرفته مواد',
              'سرقت مسلحانه از بانک و اسلحهسازی',
              'بازار اعضا، جیب‌بری و دستگاه چاپ پول',
              'زره سنگین دیفندر'
            ],
            color: 'from-[#06240e] via-[#0d471b] to-[#125c24]',
            icon: Shield,
            image: '/grovestreet.png',
            popular: true,
            badge: 'تک لیدر (۳۰ روزه)',
            category: 'roleplay'
          },
          'L-Ballas': {
            fullName: 'گنگستر Ballas',
            features: [
              'دوره ۳۰ روزه اختصاصی (تک لیدر)',
              'ظرفیت: ۱ لیدر + ۲ ممبر (مجموع ۳ نفر)',
              'بالاترین رده خلافکاری سرور',
              'دسترسی به تمام بذرهای پیشرفته مواد',
              'سرقت مسلحانه از بانک و اسلحهسازی',
              'بازار اعضا، جیب‌بری و دستگاه چاپ پول',
              'زره سنگین دیفندر'
            ],
            color: 'from-[#240624] via-[#470d47] to-[#5c125c]',
            icon: Crown,
            image: '/ballas.png',
            popular: false,
            category: 'roleplay'
          },
          'L-Ravex': {
            fullName: 'مافیا Ravex',
            features: [
              'دوره ۳۰ روزه اختصاصی (تک لیدر)',
              'ظرفیت: ۱ لیدر + ۳ ممبر (مجموع ۴ نفر)',
              'رده دوم خلافکاری سرور',
              'دسترسی به سرقت از بانک',
              'زرادخانه اسلحه و قاچاق اعضا',
              'جعل اسکناس و بذرهای تجاری'
            ],
            color: 'from-[#2e2300] via-[#594400] to-[#785c00]',
            icon: Flame,
            image: '/ravex.png',
            popular: false,
            category: 'roleplay'
          },
          'L-Zyrex': {
            fullName: 'مافیا Zyrex',
            features: [
              'دوره ۳۰ روزه اختصاصی (تک لیدر)',
              'ظرفیت: ۱ لیدر + ۳ ممبر (مجموع ۴ نفر)',
              'رده دوم خلافکاری سرور',
              'دسترسی به بازار سیاه',
              'تجهیزات سنگین مافیایی',
              'کارتل اسلحه'
            ],
            color: 'from-[#00282b] via-[#004f54] to-[#006f75]',
            icon: Zap,
            image: '/zyrex.png',
            popular: false,
            category: 'roleplay'
          }
        };

        const config = rankConfig[rank.type] || {};

        return {
          type: rank.type,
          price: rank.price,
          description: rank.description,
          isSoldOut: rank.isSoldOut || false,
          daysRemaining: rank.daysRemaining || null,
          currentLeader: rank.currentLeader || null,
          ...config
        };
      });

      const uniqueRanks = mappedRanks.filter((rank, index, self) =>
        index === self.findIndex(r => r.type === rank.type)
      );
      setRanks(uniqueRanks);
    } catch (error) {
      console.error('Error fetching ranks:', error);
      toast.error('خطا در دریافت لیست رنک‌ها از سرور');
    } finally {
      setIsLoading(false);
    }
  };

  const renderRankCard = (rank) => {
    const IconComponent = rank.icon || Shield;

    return (
      <motion.div
        key={rank.type}
        whileHover={{ y: -6 }}
        className={`relative bg-white/5 border rounded-3xl overflow-hidden backdrop-blur-xl transition-all duration-300 ${
          rank.isSoldOut
            ? 'border-red-900/50 opacity-95'
            : rank.popular
            ? 'border-emerald-500 shadow-xl shadow-emerald-500/10'
            : 'border-white/10 hover:border-white/20'
        }`}
      >
        {rank.isSoldOut ? (
          <div className="absolute top-4 right-4 bg-red-600/90 text-white px-3 py-1 rounded-full text-xs font-bold z-10 shadow-lg border border-red-500/50">
            ناموجود (در اختیار پلیر)
          </div>
        ) : rank.popular ? (
          <div className="absolute top-4 right-4 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold z-10 shadow-lg">
            {rank.badge}
          </div>
        ) : rank.badge ? (
          <div className="absolute top-4 right-4 bg-cyan-600 text-white px-3 py-1 rounded-full text-xs font-bold z-10 shadow-lg">
            {rank.badge}
          </div>
        ) : null}

        <div className={`h-48 bg-gradient-to-br ${rank.color} relative overflow-hidden`}>
          <img
            src={rank.image}
            alt={rank.type}
            className="w-full h-full object-cover opacity-80"
            onError={(e) => { e.target.src = 'https://via.placeholder.com/600x400/0d2417/ffffff?text=' + (rank.fullName || rank.type); }}
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute bottom-4 left-4 text-white">
            <IconComponent className="w-8 h-8 mb-1" />
            <h3 className="text-2xl font-black">{rank.fullName || rank.type}</h3>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-black text-white">
              {rank.price.toLocaleString()} تومان
            </span>
          </div>

          <p className="text-gray-400 text-sm line-clamp-3">
            {rank.description}
          </p>

          <ul className="space-y-2 pt-2 border-t border-white/5">
            {rank.features.map((feature, index) => (
              <li key={index} className="flex items-center text-xs text-gray-300">
                <CheckCircle className="w-4 h-4 text-emerald-400 ml-2 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>

          {rank.isSoldOut ? (
            <button
              disabled
              className="w-full bg-red-950/80 border border-red-800/80 text-red-300 font-bold py-3 px-4 rounded-xl flex flex-col items-center justify-center gap-0.5 shadow-md mt-4 cursor-not-allowed opacity-90"
            >
              <span className="font-black text-sm">ناموجود (در اختیار پلیر دیگر)</span>
              {rank.daysRemaining && (
                <span className="text-[11px] font-medium text-red-400">
                  احتمال آزادی ظرف {rank.daysRemaining} روز آینده
                </span>
              )}
            </button>
          ) : (
            <button
              onClick={() => navigate(`/ranks/${rank.type}`)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm shadow-md mt-4"
            >
              <span>مشاهده جزئیات و خرید</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </motion.div>
    );
  };

  const renderCategorySection = (title, category, ranks) => {
    const categoryRanks = ranks.filter(rank => rank.category === category);
    if (categoryRanks.length === 0) return null;

    return (
      <div className="mb-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-black text-white mb-2">
            {title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-cyan-500 mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categoryRanks.map(renderRankCard)}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050811] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050811] text-white pt-24 pb-16 relative overflow-hidden" dir="rtl">
      
      {/* پس‌زمینه زنده با هاله‌های زمردی و سیان */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-emerald-900/20 rounded-full blur-[140px]" />
        <div className="absolute top-[40%] right-0 w-[600px] h-[400px] bg-cyan-900/15 rounded-full blur-[160px]" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[400px] bg-emerald-950/20 rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-black text-white mb-3"
          >
            فروشگاه رنک
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-base max-w-2xl mx-auto"
          >
            رنک مورد نظر خود را انتخاب کنید و تجربه بازی بهتری داشته باشید
          </motion.p>
        </div>

        {/* Ranks Categories */}
        {renderCategorySection('رول‌پلی', 'roleplay', ranks)}
        {renderCategorySection('بدوارز', 'bedwars', ranks)}
        {renderCategorySection('پیلارز', 'pillars', ranks)}

      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && selectedRank && (
        <RankPurchaseModal
          rank={selectedRank}
          onClose={() => setShowPurchaseModal(false)}
        />
      )}
    </div>
  );
};

export default RankShop;
